import { makeAutoObservable } from 'mobx';
import type { CrmDatabase, PendingMutation } from '../lib/db/database';

export type SyncStatus = 'idle' | 'bootstrapping' | 'syncing' | 'pushing' | 'error' | 'offline';

export class SyncStore {
  status: SyncStatus = 'idle';
  lastSyncVersion: number = 0;
  lastSyncAt: string | null = null;
  error: string | null = null;
  metadataReady: boolean = false;
  bootstrapComplete: boolean = false;
  // True when IDB meta says a prior session already bootstrapped from the server.
  // Set synchronously from IDB meta — survives page reload. Used by SyncEngine to
  // skip the bootstrap API on warm load; the separate `bootstrapComplete` flag gates
  // UI rendering and is set only after MobX stores are hydrated from IDB.
  serverBootstrapDone: boolean = false;
  pendingMutationCount: number = 0;
  private db: CrmDatabase | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setDb(db: CrmDatabase): void {
    this.db = db;
  }

  setStatus(status: SyncStatus): void {
    this.status = status;
    if (status !== 'error') this.error = null;
  }

  setError(error: string): void {
    this.status = 'error';
    this.error = error;
  }

  setSyncVersion(version: number): void {
    this.lastSyncVersion = version;
    this.lastSyncAt = new Date().toISOString();
  }

  setMetadataReady(ready: boolean): void {
    this.metadataReady = ready;
  }

  setBootstrapComplete(complete: boolean): void {
    this.bootstrapComplete = complete;
    if (complete) this.metadataReady = true;
  }

  async queueMutation(
    entity: string,
    operation: 'create' | 'update' | 'delete',
    entityId: string,
    data?: Record<string, unknown> | Partial<unknown>
  ): Promise<void> {
    if (!this.db) return;
    const mutation: PendingMutation = {
      id: crypto.randomUUID(),
      entity,
      operation,
      entityId,
      // JSON round-trip strips MobX observable proxies and non-cloneable values
      // so IndexedDB's structured-clone algorithm can store the object cleanly.
      data: data != null
        ? JSON.parse(JSON.stringify(data)) as Record<string, unknown>
        : undefined,
      clientTimestamp: new Date().toISOString(),
      retryCount: 0,
    };
    await this.db.addPendingMutation(mutation);
    this.pendingMutationCount++;
  }

  decrementPendingCount(count: number = 1): void {
    this.pendingMutationCount = Math.max(0, this.pendingMutationCount - count);
  }

  async cancelPendingMutation(entity: string, operation: string, entityId: string): Promise<void> {
    if (!this.db) return;
    const all = await this.db.getAllPendingMutations();
    const toCancel = all.filter(
      m => m.entity === entity && m.operation === operation && m.entityId === entityId
    );
    for (const m of toCancel) {
      await this.db.removePendingMutation(m.id);
      this.pendingMutationCount = Math.max(0, this.pendingMutationCount - 1);
    }
  }

  private _flushFn: (() => Promise<void>) | null = null;

  setFlushFn(fn: () => Promise<void>): void {
    this._flushFn = fn;
  }

  async flush(): Promise<void> {
    if (this._flushFn) await this._flushFn();
  }

  clear(): void {
    this.status = 'idle';
    this.lastSyncVersion = 0;
    this.lastSyncAt = null;
    this.error = null;
    this.metadataReady = false;
    this.bootstrapComplete = false;
    this.serverBootstrapDone = false;
    this.pendingMutationCount = 0;
  }
}
